import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import { StatusBadge } from "components/common/StatusBadge";
import Switch from "components/common/Switch";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { handleStatusUpdate } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { getCouponsList, updateCouponStatus } from "store/slices/couponSlice";
import { showDeleteModal } from "store/slices/modalSlice";
import { resetFilters, setFilter } from "store/slices/couponSlice";
import {
  COUPON_VISIBILITY,
  ORDER_BY_OPTIONS,
  STATUS_OPTIONS,
} from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";

const Coupons = () => {
  const { t } = useTranslation();
  const { trash, edit, noPlans } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("coupon");

  const {
    loading,
    isLoading,
    loadingItemId,
    totalPages,
    items: Coupons,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy, visibility },
  } = useSelector((state) => state.Coupons);

  useEffect(() => {
    getAllData();
  }, [canView]);
  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      status: isReset ? -1 : status,
      visibility: isReset ? "" : visibility,
      orderBy: isReset ? -1 : orderBy,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    

    dispatch(getCouponsList(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "status",
      placeholder: t("Status"),
      type: "singleSelect",
      options: STATUS_OPTIONS,
    },
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
    {
      key: "visibility",
      placeholder: t("Select Visibility"),
      type: "singleSelect",
      options: COUPON_VISIBILITY,
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Coupon Code"),
        key: "couponCode",
        accessor: (value) => (
          <div>
            <Link to={`/coupons/coupon-details?coupon_id=${value?._id}`}>
            <div>{value.couponCode}</div>
            </Link>
          </div>
        ),
      },
      {
        header: t("Visibility"),
        key: "visibility",
        render: (value) => (
          <div>
            <div>{value}</div>
          </div>
        ),
      },
      {
        header: t("Total Used Count"),
        key: "visibility",
        accessor: (value) => (
          <Link to={`/coupons/applied-coupons?coupon_id=${value?._id}`}>
          <div className="d-flex justify-content-center">
            {value?.totalRedemptions}
          </div>
        </Link>
        ),
      },
      {
        header: t("Start Date"),
        key: "startsAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("End Date"),
        key: "endsAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <StatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <>
            <Switch
              checked={item.status === 1}
              handleOnChange={() => {
                handleStatusUpdate({
                  _id: item._id,
                  status: item.status,
                  dispatch,
                  action: updateCouponStatus,
                });
              }}
              disabled={isLoading && loadingItemId === item._id}
            />
            <img
              src={edit}
              width={20}
              height={20}
              alt="edit"
              onClick={() => navigate(`/coupons/edit-coupon?id=${item._id}`)}
            />
          </>
        )}
        {canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt={t("delete")}
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete Coupon"),
                  message: t("Are you sure you want to delete this Coupon?"),
                  itemId: item._id,
                  actionType: "COUPON",
                }),
              );
            }}
          />
        )}
      </>
    ),
    [canEdit, canDelete, isLoading, loadingItemId],
  );

  if (!canView) return null;

  return (
    <div className="page-content coupons_page">
      <h4 className="page_title mb-3">{t("Coupons")}</h4>
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Coupon Code")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy, visibility }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        btnText={t("Add Coupon")}
        onBtnClick={() => navigate("/coupons/add-coupon")}
      />

      {loading && <TableShimmer rows={12} columns={6} />}

      {!loading && Coupons?.length === 0 && (
        <NoDataFound title={t("No Coupons Found")} icon={noPlans} />
      )}
      {!loading && Coupons?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={Coupons}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default Coupons;
